import { isApiOk } from '@/services/types'
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type {
  MessageNotification,
  MessageType,
  MessagePreference,
  FilterType,
} from '@/types/notifications'
import {
  fetchNotifications as serviceFetchNotifications,
  fetchUnreadCount as serviceFetchUnreadCount,
  markAsRead as serviceMarkAsRead,
  markAllAsRead as serviceMarkAllAsRead,
  deleteNotification as serviceDeleteNotification,
  batchDelete as serviceBatchDelete,
  batchMarkRead as serviceBatchMarkRead,
  toggleStar as serviceToggleStar,
  fetchExpiringSoon as serviceFetchExpiringSoon,
  fetchPreferences as serviceFetchPreferences,
  updatePreference as serviceUpdatePreference,
  batchUpdatePreferences as serviceBatchUpdatePreferences,
} from '@/services/inbox-notifications'

export const useNotificationStore = defineStore('notifications', () => {
  // ========== State ==========
  const notifications = ref<MessageNotification[]>([])
  const loading = ref(false)
  const hasMore = ref(true)
  const currentPage = ref(1)
  const filterType = ref<FilterType>('all')
  const searchKeyword = ref('')
  const selectedIds = ref<Set<string>>(new Set())
  const preferences = ref<Record<MessageType, MessagePreference>>({
    system: { userId: '', type: 'system', receive: true, badge: true, email: false },
    collaboration: { userId: '', type: 'collaboration', receive: true, badge: true, email: false },
    todo: { userId: '', type: 'todo', receive: true, badge: true, email: false },
    audit: { userId: '', type: 'audit', receive: true, badge: true, email: false },
    ai_generation: { userId: '', type: 'ai_generation', receive: true, badge: true, email: false },
  })
  const unreadTotal = ref(0)
  const expiringSoonCount = ref(0)
  /** 服务端返回的当前筛选条件下消息总数（供页头"共 X 条消息"使用） */
  const total = ref(0)
  /** 列表拉取失败原因（错误态渲染用；成功时清空）。业务级错误此前被伪装成"暂无消息"空态 */
  const fetchError = ref<string | null>(null)

  // 统一防抖定时器（L7: 合并 filter/search 的防抖为单一触发）
  let debounceTimer: ReturnType<typeof setTimeout> | null = null

  // AbortController 用于竞态处理
  let abortControllerRef: AbortController | null = null

  // 轮询相关
  let pollingIntervalId: ReturnType<typeof setInterval> | null = null
  let leavePageTimeoutId: ReturnType<typeof setTimeout> | null = null

  // ========== Getters ==========
  // 角标/未读计数统一以服务端 unreadTotal 为准（后端已按 badge 偏好过滤），
  // 不再维护基于本地列表前 20 条的 badgeCount/unreadCount，避免与全量数据不一致。

  const filteredNotifications = computed(() => {
    let list = notifications.value
    if (filterType.value === 'starred') {
      // "已标星"由服务端 filter=starred 过滤，本地仅兜底（如 mock 模式）
      list = list.filter(n => n.isStarred)
    } else if (filterType.value !== 'all') {
      list = list.filter(n => n.type === filterType.value)
    }
    if (searchKeyword.value.trim()) {
      const kw = searchKeyword.value.toLowerCase()
      list = list.filter(n =>
        n.title.toLowerCase().includes(kw) ||
        n.content.toLowerCase().includes(kw) ||
        n.sender.name.toLowerCase().includes(kw),
      )
    }
    return list
  })

  const selectedNotifications = computed(() => {
    return notifications.value.filter(n => selectedIds.value.has(n.id))
  })

  // ========== Helpers ==========
  function setLoading(value: boolean) {
    loading.value = value
  }

  /** 统一防抖触发（L7: filter/search 变更后 300ms 发起请求） */
  function debouncedFetch() {
    if (debounceTimer) clearTimeout(debounceTimer)
    debounceTimer = setTimeout(() => {
      // 防抖回调吞掉 rejection：错误经 fetchError 状态透出到列表错误态，避免 unhandled rejection
      fetchNotifications(1, 20, false).catch(() => {})
    }, 300)
  }

  // ========== Actions: Notifications ==========
  /** 返回本次拉取的业务结果（组件层据此对"追加加载/筛选重取失败但列表非空"的场景弹 toast） */
  async function fetchNotifications(page = 1, pageSize = 20, append = false) {
    // 取消之前的请求（竞态处理）(L3: signal 真正注入)
    if (abortControllerRef) {
      abortControllerRef.abort()
    }
    abortControllerRef = new AbortController()

    setLoading(true)
    try {
      const res = await serviceFetchNotifications({
        page,
        pageSize,
        filter: filterType.value === 'all' ? 'all' : filterType.value === 'starred' ? 'starred' : undefined,
        type: filterType.value !== 'all' && filterType.value !== 'starred' ? filterType.value : undefined,
        keyword: searchKeyword.value || undefined,
        signal: abortControllerRef.signal,
      })
      if (res.data) {
        if (append) {
          // L8: 支持追加模式（加载更多）
          notifications.value = [...notifications.value, ...res.data.list]
        } else {
          notifications.value = res.data.list
        }
        total.value = res.data.total
        hasMore.value = res.data.hasMore
        currentPage.value = page
        fetchError.value = null
        // 注意：unreadTotal 不从这里取——列表接口按分页/筛选返回，本页未读数不代表全局；
        // 全局未读/角标计数统一由 refreshUnreadCount()（服务端、已按偏好过滤）维护。
      } else {
        // 业务级失败（4xx 等）：置错误供列表错误态渲染（列表为空时），
        // 列表非空时由组件层消费返回值弹 toast（评审跟进⑤）
        fetchError.value = res.message || '消息加载失败，请稍后重试'
      }
      return res
    } catch (e) {
      const error = e instanceof Error ? e.message : '消息加载失败，请稍后重试'
      fetchError.value = error
      return { code: 500, message: error }
    } finally {
      setLoading(false)
      abortControllerRef = null
    }
  }

  async function refreshUnreadCount() {
    const res = await serviceFetchUnreadCount()
    if (res.data) {
      unreadTotal.value = res.data.total
    }
  }  async function markAsRead(ids: string[]) {
    if (ids.length === 1) {
      const res = await serviceMarkAsRead(ids[0])
      if (isApiOk(res)) {
        const n = notifications.value.find(n => n.id === ids[0])
        if (n) {
          n.isRead = true
          n.readAt = new Date()
        }
        selectedIds.value = new Set([...selectedIds.value].filter(id => !ids.includes(id)))
        await refreshUnreadCount()
      }
      return res
    }
    // 多条使用批量已读
    const res = await serviceBatchMarkRead(ids)
    if (isApiOk(res)) {
      notifications.value.forEach(n => {
        if (ids.includes(n.id)) {
          n.isRead = true
          n.readAt = new Date()
        }
      })
      selectedIds.value = new Set([...selectedIds.value].filter(id => !ids.includes(id)))
      await refreshUnreadCount()
    }
    return res
  }

  async function markAllAsRead() {
    const res = await serviceMarkAllAsRead()
    if (isApiOk(res)) {
      notifications.value.forEach(n => {
        n.isRead = true
        n.readAt = new Date()
      })
      selectedIds.value.clear()
      await refreshUnreadCount()
    }
    return res
  }

  async function deleteNotifications(ids: string[]) {
    if (ids.length === 1) {
      const res = await serviceDeleteNotification(ids[0])
      if (isApiOk(res)) {
        notifications.value = notifications.value.filter(n => n.id !== ids[0])
        selectedIds.value = new Set([...selectedIds.value].filter(id => id !== ids[0]))
        total.value = Math.max(0, total.value - 1)
        await refreshUnreadCount()
      }
      return res
    }
    const res = await serviceBatchDelete(ids)
    if (isApiOk(res)) {
      notifications.value = notifications.value.filter(n => !ids.includes(n.id))
      selectedIds.value = new Set([...selectedIds.value].filter(id => !ids.includes(id)))
      const deleted = res.data?.deleted ?? ids.length
      total.value = Math.max(0, total.value - deleted)
      await refreshUnreadCount()
    }
    return res
  }

  async function clearReadNotifications() {
    const readIds = notifications.value.filter(n => n.isRead).map(n => n.id)
    if (readIds.length === 0) return { code: 200 }
    return await deleteNotifications(readIds)
  }

  /** 标星/取消标星：本地同步 isStarred，静音返回（store 层不弹 toast） */
  async function toggleStar(id: string, starred: boolean) {
    const res = await serviceToggleStar(id, starred)
    if (isApiOk(res)) {
      const n = notifications.value.find(n => n.id === id)
      if (n) n.isStarred = starred
    }
    return res
  }

  async function fetchExpiringSoon() {
    const res = await serviceFetchExpiringSoon()
    if (res.data) {
      expiringSoonCount.value = res.data.count
    }
  }

  // ========== Actions: Selection ==========
  function toggleSelection(id: string) {
    const next = new Set(selectedIds.value)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    selectedIds.value = next
  }

  function selectAll() {
    selectedIds.value = new Set(filteredNotifications.value.map(n => n.id))
  }

  function clearSelection() {
    selectedIds.value.clear()
  }

  // ========== Actions: Filter & Search (L7: 统一防抖) ==========
  function setFilterType(type: FilterType) {
    filterType.value = type
    hasMore.value = true
    debouncedFetch()
  }

  function setSearchKeyword(keyword: string) {
    searchKeyword.value = keyword
    hasMore.value = true
    debouncedFetch()
  }

  // ========== Actions: Preferences ==========
  async function fetchPreferences() {
    const res = await serviceFetchPreferences()
    if (res.data) {
      const map: Record<MessageType, MessagePreference> = {
        system: { userId: '', type: 'system', receive: true, badge: true, email: false },
        collaboration: { userId: '', type: 'collaboration', receive: true, badge: true, email: false },
        todo: { userId: '', type: 'todo', receive: true, badge: true, email: false },
        audit: { userId: '', type: 'audit', receive: true, badge: true, email: false },
        ai_generation: { userId: '', type: 'ai_generation', receive: true, badge: true, email: false },
      }
      for (const p of res.data.preferences) {
        map[p.type] = p
      }
      preferences.value = map
    }
  }

  async function updatePreference(type: MessageType, patch: { receive?: boolean; badge?: boolean; email?: boolean }) {
    const res = await serviceUpdatePreference(type, patch)
    if (res.data) {
      preferences.value[type] = res.data
    }
    return res
  }

  async function batchUpdatePreferences(updates: Array<{ type: MessageType; receive?: boolean; badge?: boolean; email?: boolean }>) {
    const res = await serviceBatchUpdatePreferences(updates)
    if (isApiOk(res)) {
      for (const u of updates) {
        if (u.receive !== undefined) preferences.value[u.type].receive = u.receive
        if (u.badge !== undefined) preferences.value[u.type].badge = u.badge
        if (u.email !== undefined) preferences.value[u.type].email = u.email
      }
    }
    return res
  }

  // ========== 轮询 (US7) ==========
  let visibilityListenerAttached = false

  function startPolling(intervalMs = 30000) {
    // 防止重复启动
    if (pollingIntervalId) return
    pollingIntervalId = setInterval(() => {
      // 轮询回调必须吞掉 rejection：会话失效时刷新令牌也会失败，
      // 不 catch 会在控制台留一条未处理的 Promise 拒绝（与 401 叠成双份报错）
      refreshUnreadCount().catch(() => {})
      fetchExpiringSoon().catch(() => {})
    }, intervalMs)
    // 监听页面可见性，离开时停止、返回时恢复；避免重复注册导致监听器累积
    if (typeof document !== 'undefined' && !visibilityListenerAttached) {
      document.addEventListener('visibilitychange', handleVisibilityChange)
      visibilityListenerAttached = true
    }
  }

  function stopPolling() {
    if (pollingIntervalId) {
      clearInterval(pollingIntervalId)
      pollingIntervalId = null
    }
    if (leavePageTimeoutId) {
      clearTimeout(leavePageTimeoutId)
      leavePageTimeoutId = null
    }
    if (typeof document !== 'undefined' && visibilityListenerAttached) {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      visibilityListenerAttached = false
    }
  }

  function handleVisibilityChange() {
    if (typeof document === 'undefined') return
    if (document.hidden) {
      // 离开页面：5 秒倒计时后停止轮询
      leavePageTimeoutId = setTimeout(() => {
        if (pollingIntervalId) {
          clearInterval(pollingIntervalId)
          pollingIntervalId = null
        }
      }, 5000)
    } else {
      // 返回页面：取消倒计时，立即刷新并重启轮询
      if (leavePageTimeoutId) {
        clearTimeout(leavePageTimeoutId)
        leavePageTimeoutId = null
      }
      if (!pollingIntervalId) {
        refreshUnreadCount().catch(() => {})
        fetchExpiringSoon().catch(() => {})
        startPolling()
      }
    }
  }

  // ========== Initialization ==========
  async function initialize() {
    // 逐段容错：任一请求失败不阻断后续步骤，保证 startPolling 必达
    //（否则登录瞬时的接口错误会让角标在整个会话期间失去自动刷新）
    try {
      await fetchPreferences()
    } catch {
      // 偏好设置请求支持空数据场景
    }
    try {
      await fetchNotifications(1, 20, false)
    } catch {
      // 列表拉取失败时仍继续初始化未读数与轮询
    }
    try {
      await refreshUnreadCount()
    } catch {
      // 未读数拉取失败，30s 轮询会自动重试
    }
    try {
      await fetchExpiringSoon()
    } catch {
      // 即将过期计数失败可忽略
    }
    // 启动未读数自动轮询（30s）
    startPolling()
  }

  return {
    // State
    notifications,
    loading,
    hasMore,
    currentPage,
    filterType,
    searchKeyword,
    selectedIds,
    preferences,
    unreadTotal,
    total,
    expiringSoonCount,
    fetchError,
    // Getters
    filteredNotifications,
    selectedNotifications,
    // Actions
    fetchNotifications,
    refreshUnreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotifications,
    clearReadNotifications,
    toggleStar,
    fetchExpiringSoon,
    toggleSelection,
    selectAll,
    clearSelection,
    setFilterType,
    setSearchKeyword,
    fetchPreferences,
    updatePreference,
    batchUpdatePreferences,
    initialize,
    // Polling
    startPolling,
    stopPolling,
  }
})
